import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { TaxonomyVocabularyService, ViewService } from 'src/app/services/ngx-drupal7-services';
import { StorageService } from 'src/app/services/storage.service';
import * as moment from 'moment';

export const VOCABULARY_CORE_SIZE = 46; // "core_size";
export const VOCABULARY_WAREHOUSE = 50; // "warehouse_location";
export const VOCABULARY_BAY_LOCATION = 44; // "bay_location";
export const VOCABULARY_MATERIAL_TYPE = 47; // "material_type";
export const VOCABULARY_AUDIT_OPTIONS = 57; // "audit_options";
export const VOCABULARY_PAPER_COLORS = 2; // "paper_colors";
export const VOCABULARY_CORE_COLORS = 3; // "core_colors";
export const VOCABULARY_BACKING_COLORS = 8; // "backing_colors";
export const VOCABULARY_ROLL_COLORS = 60; // "roll_colors";
export const VOCABULARY_ROLL_USES = 59; // "roll_uses";
export const VOCABULARY_CALIPER_2 = 11; // "caliper_2";
export const VOCABULARY_SIZE_CALIPER = 18; // "size_caliper";

// Customers are not loaded as a vocabulary tree: nal_customers holds ~2800 terms
// and only the child (ship-to) terms are selectable, so we use the view that
// already filters to those and returns them with the parent name prefixed.
const VIEW_CUSTOMER_TERMS = 'customer_select_terms';

const STORAGE_KEY_PREFIX = 'vocabularies-';

@Injectable({
    providedIn: 'root',
})
export class VocabularyService {
    // TODO : Convert these individual vars to a single object
    core_sizes = [];
    warehouses = [];
    material_types = [];
    bays = [];
    audit_options = [];
    paper_colors = [];
    core_colors = [];
    backing_colors = [];
    roll_colors = [];
    roll_uses = [];
    calipers = [];
    sizes = [];
    customers = [];

    constructor(
        private storage: StorageService,
        private taxonomyVocabularyService: TaxonomyVocabularyService,
        private viewService: ViewService,
    ) {
        storage.get(STORAGE_KEY_PREFIX + 'core_sizes').then((val) => {
            if (val) {
                this.core_sizes = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'warehouses').then((val) => {
            if (val) {
                this.warehouses = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'material_types').then((val) => {
            if (val) {
                this.material_types = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'bays').then((val) => {
            if (val) {
                this.bays = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'audit_options').then((val) => {
            if (val) {
                this.audit_options = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'paper_colors').then((val) => {
            if (val) {
                this.paper_colors = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'core_colors').then((val) => {
            if (val) {
                this.core_colors = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'backing_colors').then((val) => {
            if (val) {
                this.backing_colors = val;
            }
        });

        storage.get(STORAGE_KEY_PREFIX + 'roll_colors').then((val) => {
            if (val) {
                this.roll_colors = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'roll_uses').then((val) => {
            if (val) {
                this.roll_uses = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'calipers').then((val) => {
            if (val) {
                this.calipers = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'sizes').then((val) => {
            if (val) {
                this.sizes = val;
            }
        });
        storage.get(STORAGE_KEY_PREFIX + 'customers').then((val) => {
            if (val) {
                this.customers = val;
            }
        });
    }

    getVocabulary(vocabulary, vid): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.isFresh(vocabulary).then(
                (isFresh) => {
                    if (isFresh) {
                        resolve(this[vocabulary]);
                        // console.log("Provide local " + vocabulary, this[vocabulary]);
                    } else {
                        this.taxonomyVocabularyService
                            .getTaxonomyVocabularyTree(vid)
                            .toPromise()
                            .then(
                                (items) => {
                                    this[vocabulary] = items;
                                    this.storage.set(STORAGE_KEY_PREFIX + vocabulary, items);
                                    const now = new Date();
                                    this.storage.set(
                                        STORAGE_KEY_PREFIX + vocabulary + '-refreshed',
                                        now,
                                    );
                                    // console.log("Get fresh " + vocabulary, items);
                                    resolve(items);
                                },
                                (error) => {
                                    reject(error);
                                },
                            );
                    }
                },
                (error) => {
                    reject(error);
                },
            );
        });
    }

    getCoreSizes(): Promise<any> {
        return this.getVocabulary('core_sizes', VOCABULARY_CORE_SIZE);
    }
    getWarehouseLocations(): Promise<any> {
        return this.getVocabulary('warehouses', VOCABULARY_WAREHOUSE);
    }
    getBayLocations(): Promise<any> {
        return this.getVocabulary('bays', VOCABULARY_BAY_LOCATION);
    }
    getMaterialTypes(): Promise<any> {
        return this.getVocabulary('material_types', VOCABULARY_MATERIAL_TYPE);
    }
    getAuditOptions(): Promise<any> {
        return this.getVocabulary('audit_options', VOCABULARY_AUDIT_OPTIONS);
    }
    getPaperColors(): Promise<any> {
        return this.getVocabulary('paper_colors', VOCABULARY_PAPER_COLORS);
    }
    getCoreColors(): Promise<any> {
        return this.getVocabulary('core_colors', VOCABULARY_CORE_COLORS);
    }
    getBackingColors(): Promise<any> {
        return this.getVocabulary('backing_colors', VOCABULARY_BACKING_COLORS);
    }
    getRollColors(): Promise<any> {
        return this.getVocabulary('roll_colors', VOCABULARY_ROLL_COLORS);
    }
    getRollUses(): Promise<any> {
        return this.getVocabulary('roll_uses', VOCABULARY_ROLL_USES);
    }
    getCalipers(): Promise<any> {
        return this.getVocabulary('calipers', VOCABULARY_CALIPER_2);
    }
    getSizes(): Promise<any> {
        return this.getVocabulary('sizes', VOCABULARY_SIZE_CALIPER);
    }

    /**
     * Ship-to customer terms, from the customer_select_terms view.
     * The view renders the term name as a link, so the markup is stripped here.
     */
    getCustomers(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.isFresh('customers').then((isFresh) => {
                if (isFresh) {
                    resolve(this.customers);
                    return;
                }
                this.viewService
                    .getView(VIEW_CUSTOMER_TERMS, { display_id: 'default' })
                    .toPromise()
                    .then((rows: any) => {
                        // A term with two parents is listed once per parent, which is
                        // meaningful (the prefix differs), but exact repeats and
                        // unnamed terms are not worth showing in the picker.
                        const seen = {};
                        const items = [];
                        (rows || []).forEach((row) => {
                            const name = this.cleanTermName(row.name);
                            const key = row.tid + '|' + name;
                            if (!name || seen[key]) {
                                return;
                            }
                            seen[key] = true;
                            items.push({ tid: row.tid, name });
                        });
                        this.customers = items;
                        this.storage.set(STORAGE_KEY_PREFIX + 'customers', items);
                        this.storage.set(STORAGE_KEY_PREFIX + 'customers-refreshed', new Date());
                        resolve(items);
                    }, reject);
            }, reject);
        });
    }

    /**
     * Views renders term names as markup; the pickers need plain text.
     */
    cleanTermName(raw): string {
        if (!raw) {
            return '';
        }
        const stripped = String(raw).replace(/<[^>]*>/g, '');
        const decoder = document.createElement('textarea');
        decoder.innerHTML = stripped;
        return decoder.value.trim();
    }

    isFresh(vocabulary): Promise<Boolean> {
        return new Promise<Boolean>((resolve, reject) => {
            this.storage.get(STORAGE_KEY_PREFIX + vocabulary + '-refreshed').then((val) => {
                if (val) {
                    const when = moment(val);
                    if (
                        val == null ||
                        when.add(environment.freshnessVocab, 'seconds').isBefore(moment())
                    ) {
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                } else {
                    resolve(false);
                }
            });
        });
    }
}
